import { OnboardPayload } from "#src/api/Onboard.ts";
import {
  balanceAccounts,
  balanceAccountsVariables,
  bulkOrganization,
  bulkOrganizationVariables,
  createOrganization,
  createOrganizationVariables,
  currentOrganization,
  currentOrganizationVariables,
  deleteOrganization,
  deleteOrganizationVariables,
  editBulkOrganization,
  editBulkOrganizationVariables,
  exportBalanceAccounts,
  exportBalanceAccountsVariables,
  exportPayout,
  exportPayoutVariables,
  financialReport,
  financialReportVariables,
  fullOrganization,
  fullOrganizationVariables,
  importData,
  importDataVariables,
  kioskMode,
  kioskModeVariables,
  organization,
  organizationImports,
  organizationImportsVariables,
  organizationVariables,
  organizationsList,
  organizationsListVariables,
  payouts,
  payoutsVariables,
  revenueOverTime,
  revenueOverTimeVariables,
  syncUsaSwimming,
  syncUsaSwimmingVariables,
  topUpBalanceAccount,
  topUpBalanceAccountVariables,
  unreadItems,
  unreadItemsVariables,
  updateOrganization,
  updateOrganizationVariables,
  usaSwimming,
  usaSwimmingVariables,
  type superAdminUpgrades,
  type superAdminUpgradesVariables,
} from "#types/code-generator.ts";
import type { TypedDocumentNode } from "#types/graphql.d.ts";
import { gql } from "graphql-tag";

const OrganizationPayload = gql`
  fragment OrganizationPayload on Organization {
    active
    address1
    address2
    addDepartmentsDuringOnboarding
    annualPrice
    balanceAccounts {
      id
      balanceAccountId
      transferInstrumentId
      default
      accountHolderId
    }
    betaAccess
    captynPlan
    chargeStartDate
    chargeAnnualStartDate
    city
    color
    contactName
    createdAt
    departments(orderBy: { order: asc }) {
      id
      active
      allowMultipleCoupons
      annualFeeAmount
      annualFeeAtBeginningOfYear
      annualFeeBillingCategory {
        id
        title
      }
      annualFeeCanBeAppliedAtOfferingLevel
      annualFeeMax
      annualFeeType
      balanceAccountId
      contactName
      createdAt
      default
      email
      filters
      order
      meetAccess
      programs {
        id
        title
      }
      phone
      settingOnboards
      showProgramDescription
      tags(orderBy: { order: asc }) {
        id
        hideInFilters
        order
        title
      }
      stripeExpressAccountId
      title
      updatedAt
      users {
        id
        level
        firstName
        lastName
      }
    }
    description
    email
    emailSetting {
      emailType
      emailDomain
    }
    events {
      createdAt
      type
      value
    }
    fees {
      id
      amount
      amountType
      availableForAllOfferings
      billingCategory {
        id
      }
      counts {
        offerings
      }
      createdAt
      createdBy {
        id
        firstName
        lastName
      }
      enabledByDefault
      order
      title
    }
    gracePeriod
    gracePeriodType
    id
    integrations
    lastFailedPayment
    logo {
      id
      url
      name
    }
    meetGenderTerminology
    meetTeamFullName
    meetTeamShortName
    meetTeamAbbreviation
    meetLSC
    monthlyPrice
    nextAnnualInvoice
    nextMonthlyInvoice
    note
    now
    owner {
      id
      balance
      firstName
      lastName
      level
      stripeCustomerId
      stripeToken
      adyenToken
    }
    paymentDisplay
    paymentProcessing {
      id
      achCredit
      achCreditBillingCategory {
        id
        title
      }
      achFee
      achTransactionFee
      allowOrgManageSettlementDelay
      additionalCreditTransactionFee
      creditFee
      creditTransactionFee
      disputeFee
      enableAch
      enableRefundFee
      enableRefundCushion
      enableRefundFunding
      lastSettlementDelayChange
      lastSettlementDelayChangeBy {
        id
        firstName
        lastName
      }
      maxAchFee
      minSettlementDelayChange
      minSettlementDelayChangeType
      refundCushion
      refundCushionMax
      refundCushionMin
      refundCushionWindowDays
      refundFee
      refundFundingMin
      refundTransactionFee
      settlementDelay
    }
    paymentSettings {
      legalEntityId
    }
    phone
    registrationRestriction {
      id
      waitlistPerAccount
      waitlistPerParticipant
      trainingGroupsPerParticipant
    }
    requiredFields
    score
    socialMedia {
      facebook
      twitter
      instagram
      linkedIn
      tikTok
    }
    solo
    state
    status
    statementDescriptor
    storeUrl
    stripe
    stripeAlert
    stripeAccountId
    stripeExpressAccountId
    stripeExpressAccounts {
      id
      stripeExpressAccountId
      default
    }
    stripeFee
    stripeExpressFee
    stripeTransactionFee
    subdomain
    terminology {
      private
      evaluation
      administrator
      staff
      customer
    }
    tags {
      id
      title
    }
    timezone
    title
    updatedAt
    upgradeTerminal {
      showAdminBanner
      showMenuBanner
      dismissedAdminBanner
      dismissedMenuBanner
    }
    usaSwimmingClubId
    usaSwimmingClubName
    zip
  }
`;

const FullOrganizationPayload = gql`
  fragment FullOrganizationPayload on Organization {
    ...OrganizationPayload
    activeAt
    accountManager {
      id
      firstName
      lastName
    }
    addDepartmentsDuringOnboarding
    agreements {
      id
    }
    annualPrice
    captynPlan
    chargeStartDate
    chargeAnnualStartDate
    classes {
      id
      status
    }
    commissionSettings {
      id
      amount
      user {
        id
        firstName
        lastName
      }
      startDate
      endDate
    }
    compare {
      date
      total {
        amount
      }
      captyn {
        amount
      }
      captynCharges {
        amount
      }
      commissions {
        userId
        user
        amount
      }
      refunds {
        amount
        count
      }
    }
    counts {
      accounts
      accountsParticipants
      accountsAdmin
      accountsStaff
      averageInvoice
      registrations
      updated
      invoices
      sms
    }
    createdAt
    current {
      date
      total {
        amount
      }
      captyn {
        amount
      }
      captynCharges {
        amount
      }
      commissions {
        userId
        user
        amount
      }
      refunds {
        amount
        count
      }
    }
    disputeFee
    events {
      id
      createdAt
      date
      by {
        firstName
        lastName
      }
      to {
        firstName
        lastName
      }
      type
      value
    }
    gracePeriod
    gracePeriodType
    integrations
    lastContact
    lastContactBy {
      id
      firstName
      lastName
    }
    lastFailedPayment
    locations {
      id
    }
    monthlyPrice
    needs {
      id
      title
    }
    nextMonthlyInvoice
    nextAnnualInvoice
    offerings {
      id
      status
    }
    owner {
      id
      balance
      firstName
      invoices {
        id
        status
        invoiceItems {
          id
          description
          amount
          captyn
          credits {
            id
            amount
          }
          payments {
            id
            amount
            payment {
              id
              stripeChargeId
              amount
            }
          }
        }
        createdAt
        invoiceDate
      }
      lastName
      email
      stripeCustomerId
      stripeToken
      adyenToken
    }
    onboards {
      ...OnboardPayload
    }
    pipedriveUrl
    planContent
    priceAdjustments {
      id
      date
      fee
    }
    projectedCaptynRevenue
    projectedFirstPaidRegistration
    projectedOperatingMonths
    projectedRegistrations
    projectedRevenue
    referralName
    salesPerson {
      id
      firstName
      lastName
    }
    signupInformation
    status
    statementDescriptor
    stripe
    tags {
      id
      title
    }
    nativeApps {
      id
      type
      status
      setupFee
      billingMethod
      amount
      addToInvoice
      note
      appData
      developerAccountAlert
      developerAccountSetup
      needsScreenshots
      logo
      submittedAt
    }
    upgradeNativeApp {
      id
      offerTypes
      offerBundle
      bundleDiscount
      details
    }
    upgradeWebsite {
      id
      status
      setupFee
      billingMethod
      amount
      addToInvoice
      details
      url
    }
    usaSwimmingClubId
    website
    websiteIncluded
  }
  ${OrganizationPayload}
  ${OnboardPayload}
`;

export const UpdateOrganizationMutation = gql`
  mutation updateOrganization(
    $data: OrganizationCreateInput!
    $where: UniqueInput!
  ) {
    updateOrganization(data: $data, where: $where) {
      ...OrganizationPayload
    }
  }
  ${OrganizationPayload}
` as TypedDocumentNode<updateOrganization, updateOrganizationVariables>;

export const DeleteOrganizationMutation = gql`
  mutation deleteOrganization($id: String!) {
    deleteOrganization(where: { id: $id }) {
      id
    }
  }
` as TypedDocumentNode<deleteOrganization, deleteOrganizationVariables>;

export const CreateOrganizationMutation = gql`
  mutation createOrganization($data: OrganizationCreateInput!) {
    createOrganization(data: $data) {
      ...OrganizationPayload
    }
  }
  ${OrganizationPayload}
` as TypedDocumentNode<createOrganization, createOrganizationVariables>;

export const OrganizationsQuery = gql`
  query organizationsList {
    organizations(
      where: { subdomain: { notIn: ["app", "app-dev"] } }
      orderBy: { title: asc }
    ) {
      accountManager {
        id
      }
      betaAccess
      city
      color
      contactName
      createdAt
      description
      email
      id
      logo {
        id
        url
      }
      meetLSC
      meetTeamAbbreviation
      note
      salesPerson {
        id
      }
      state
      status
      stripeAccountId
      subdomain
      title
    }
  }
` as TypedDocumentNode<organizationsList, organizationsListVariables>;

export const OrganizationQuery = gql`
  query organization($id: String!) {
    organization(where: { id: $id }) {
      ...OrganizationPayload
    }
  }
  ${OrganizationPayload}
` as TypedDocumentNode<organization, organizationVariables>;

export const FullOrganizationQuery = gql`
  query fullOrganization($id: String!, $filter: String) {
    organization(where: { id: $id }, filter: $filter) {
      ...FullOrganizationPayload
    }
  }
  ${FullOrganizationPayload}
` as TypedDocumentNode<fullOrganization, fullOrganizationVariables>;

export const RevenueOverTimeQuery = gql`
  query revenueOverTime($organization: ID!) {
    revenueOverTime(organization: $organization)
  }
` as TypedDocumentNode<revenueOverTime, revenueOverTimeVariables>;

export const FinancialReportQuery = gql`
  query financialReport($where: FinancialReportInput!) {
    financialReport(where: $where) {
      csv
      invoiceItems
      refundsIssued
      totalCharged
      totalTransactions
    }
  }
` as TypedDocumentNode<financialReport, financialReportVariables>;

export const PayoutsQuery = gql`
  query payouts($where: FinancialReportInput!, $skip: Int, $take: Int) {
    payouts(where: $where, skip: $skip, take: $take) {
      id
      amount
      date
      created
      stripeAccountId
      stripe
    }
  }
` as TypedDocumentNode<payouts, payoutsVariables>;

export const CurrentOrganizationQuery = gql`
  query currentOrganization($subdomain: String!) {
    organization(where: { subdomain: $subdomain, status: { not: Suspended } }) {
      ...OrganizationPayload
    }
  }
  ${OrganizationPayload}
` as TypedDocumentNode<currentOrganization, currentOrganizationVariables>;

export const UnreadItemsQuery = gql`
  query unreadItems($organization: ID!, $departments: [ID]) {
    unreadItems(organization: $organization, departments: $departments) {
      allClasses
      allOfferings
      customerServiceItems
      enrolledParticipants
      inProgressClasses
      inProgressMeets
      inProgressPrivateSeries
      inProgressMembershipOfferings
      allMembershipOfferings
      now
      passOfferings
      pendingGroupRegistrations
      pendingInvoiceItems
      pendingMemberships
      pendingPrivateSeries
      postedPrivateSeries
      upcomingClasses
      upcomingMeets
      waitlistAvailableClasses
    }
  }
` as TypedDocumentNode<unreadItems, unreadItemsVariables>;

export const UnreadItemsUpdatedSubscription = gql`
  subscription unreadItemsUpdated($organization: ID!, $departments: [ID]) {
    unreadItemsUpdated(organization: $organization, departments: $departments) {
      allClasses
      allOfferings
      customerServiceItems
      enrolledParticipants
      inProgressClasses
      inProgressMeets
      inProgressPrivateSeries
      inProgressMembershipOfferings
      allMembershipOfferings
      now
      passOfferings
      pendingGroupRegistrations
      pendingInvoiceItems
      pendingMemberships
      pendingPrivateSeries
      postedPrivateSeries
      upcomingClasses
      upcomingMeets
      waitlistAvailableClasses
    }
  }
`;

export const ImportDataMutation = gql`
  mutation importData($data: ImportDataInput!) {
    importData(data: $data)
  }
` as TypedDocumentNode<importData, importDataVariables>;

export const BulkOrganizationQuery = gql`
  query bulkOrganization($filter: String) {
    bulkOrganization(filter: $filter) {
      needs
      tags
      total
    }
  }
` as TypedDocumentNode<bulkOrganization, bulkOrganizationVariables>;

export const EditBulkOrganizationMutation = gql`
  mutation editBulkOrganization(
    $data: BulkOrganizationCreateInput!
    $filter: String
  ) {
    editBulkOrganization(data: $data, filter: $filter) {
      count
    }
  }
` as TypedDocumentNode<editBulkOrganization, editBulkOrganizationVariables>;

export const SyncUsaSwimmingMutation = gql`
  mutation syncUsaSwimming(
    $organizationId: ID!
    $customerServiceItemId: ID
    $sendEmail: Boolean!
    $disconnect: Boolean
    $reset: Boolean
  ) {
    syncUsaSwimming(
      organizationId: $organizationId
      customerServiceItemId: $customerServiceItemId
      sendEmail: $sendEmail
      disconnect: $disconnect
      reset: $reset
    ) {
      offeringId
    }
  }
` as TypedDocumentNode<syncUsaSwimming, syncUsaSwimmingVariables>;

export const BalanceAccountsQuery = gql`
  query balanceAccounts($filter: String, $balanceAccount: ID) {
    balanceAccounts(filter: $filter, balanceAccount: $balanceAccount) {
      id
      balanceAccountId
      createdAt
      default
      organization {
        id
        balanceAccounts {
          id
        }
        departments {
          id
          title
          balanceAccountId
        }
        title
        subdomain
        logo {
          url
        }
        city
        state
        paymentProcessing {
          enableRefundCushion
        }
      }
      pending
      available
      balance
      status
      dateNegative
      refundsAuthorized
      refundCushion
      testDebit
      verified
      pendingConnection
      accountHolderId
      lastTopUp
    }
  }
` as TypedDocumentNode<balanceAccounts, balanceAccountsVariables>;

export const ExportBalanceAccountsMutation = gql`
  mutation exportBalanceAccounts($filter: String) {
    exportBalanceAccounts(filter: $filter)
  }
` as TypedDocumentNode<exportBalanceAccounts, exportBalanceAccountsVariables>;

export const KioskModeMutation = gql`
  mutation kioskMode($organizationId: ID!) {
    kioskMode(organizationId: $organizationId)
  }
` as TypedDocumentNode<kioskMode, kioskModeVariables>;

export const USASwimmingQuery = gql`
  query usaSwimming($organizationId: ID!) {
    usaSwimming(organizationId: $organizationId)
  }
` as TypedDocumentNode<usaSwimming, usaSwimmingVariables>;

export const ExportPayoutMutation = gql`
  mutation exportPayout($where: FinancialReportInput!) {
    exportPayout(where: $where)
  }
` as TypedDocumentNode<exportPayout, exportPayoutVariables>;

export const TopUpBalanceAccountMutation = gql`
  mutation topUpBalanceAccount($balanceAccountId: ID!, $amount: Int!) {
    topUpBalanceAccount(balanceAccountId: $balanceAccountId, amount: $amount) {
      id
      balanceAccountId
      createdAt
      organization {
        id
        title
        subdomain
      }
      pending
      available
      balance
      status
      dateNegative
    }
  }
` as TypedDocumentNode<topUpBalanceAccount, topUpBalanceAccountVariables>;

export const OrganizationImportsQuery = gql`
  query organizationImports($organizationId: String!) {
    organization(where: { id: $organizationId }) {
      id
      imports {
        id
        createdAt
        name
        messages
        type
        url
      }
    }
  }
` as TypedDocumentNode<organizationImports, organizationImportsVariables>;

export const SuperAdminUpgradesQuery = gql`
  query superAdminUpgrades($filter: String) {
    upgrades(filter: $filter) {
      id
      organization {
        id
        title
        subdomain
        logo {
          url
        }
        city
        state
      }
      type
      status
      price
      frequency
      viewable
      url
      alert
    }
  }
` as TypedDocumentNode<superAdminUpgrades, superAdminUpgradesVariables>;
